import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { requiresAuth: false, guestOnly: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/auth/Register.vue'),
    meta: { requiresAuth: false, guestOnly: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPassword.vue'),
    meta: { requiresAuth: false, guestOnly: true }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/auth/ResetPassword.vue'),
    meta: { requiresAuth: false, guestOnly: true }
  },
  {
    path: '/auth/callback',
    name: 'OAuthCallback',
    component: () => import('@/views/auth/OAuthCallback.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/dashboard/Dashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/businesses',
    name: 'Businesses',
    component: () => import('@/views/businesses/Businesses.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/businesses/:id',
    name: 'BusinessDetail',
    component: () => import('@/views/businesses/BusinessDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/widgets',
    name: 'Widgets',
    component: () => import('@/views/widgets/Widgets.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/widgets/create',
    name: 'CreateWidget',
    component: () => import('@/views/widgets/CreateWidget.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/widgets/:id',
    name: 'WidgetDetail',
    component: () => import('@/views/widgets/WidgetDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/widgets/:id/edit',
    name: 'EditWidget',
    component: () => import('@/views/widgets/EditWidget.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/reviews',
    name: 'Reviews',
    component: () => import('@/views/Reviews.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    redirect: '/account/profile'
  },
  {
    path: '/account/profile',
    name: 'AccountProfile',
    component: () => import('@/views/account/Account.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/account/billing',
    name: 'AccountBilling',
    component: () => import('@/views/account/Account.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/payment-history',
    name: 'PaymentHistory',
    component: () => import('@/views/PaymentHistory.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/payment/subscription/success',
    name: 'PaymentSuccess',
    component: () => import('@/views/payment/PaymentSuccess.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/payment/subscription/cancel',
    name: 'PaymentCancel',
    component: () => import('@/views/payment/PaymentCancel.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: () => import('@/views/Pricing.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/features',
    name: 'Features',
    component: () => import('@/views/Features.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('@/views/Contact.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/analytics',
    name: 'Analytics',
    component: () => import('@/views/Analytics.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/demo',
    name: 'Demo',
    component: () => import('@/views/Demo.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/help',
    name: 'Help',
    component: () => import('@/views/Help.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('@/views/Privacy.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('@/views/Terms.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/embed/:id',
    name: 'Embed',
    component: () => import('@/views/Embed.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/demo-embed',
    name: 'DemoEmbed',
    component: () => import('@/views/DemoEmbed.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  
  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }
  
  // Check if route is guest-only (like login/register)
  if (to.meta.guestOnly && authStore.isAuthenticated) {
    next('/dashboard')
    return
  }
  
  next()
})

export default router